import { useEffect, useState } from 'react'
import { getVenueIdFromPath } from 'src/components/ollie/ollieVenueRoutes'
import { getOllieApiError } from 'src/features/OllieSlice'
import {
  OLLIE_ASK_LIMIT_FRESH_MS,
  isOllieUsageReadAtLimit,
} from 'src/utils/ollieAskLimit'
import { API } from 'src/utils/interceptor'

/** venueId → { atLimit, readAt, pending } */
const usageReads = new Map()
const listeners = new Set()

const remember = (venueId, atLimit) => {
  usageReads.set(venueId, { atLimit, readAt: Date.now() })
  listeners.forEach(listener => listener(venueId, atLimit))
}

/** The panel found out on open / send (`limitReached`); every link hears it. */
export const noteOllieLimitState = (venueId, atLimit) => {
  if (!venueId) return
  const id = String(venueId)
  if (usageReads.get(id)?.atLimit === Boolean(atLimit)) return
  remember(id, Boolean(atLimit))
}

const readOllieAtLimit = venueId => {
  const cached = usageReads.get(venueId)
  if (cached?.pending) return cached.pending
  if (cached && Date.now() - cached.readAt < OLLIE_ASK_LIMIT_FRESH_MS) {
    return Promise.resolve(cached.atLimit)
  }
  const pending = API.get('api2', `ollie/usage/${venueId}`)
    .then(data => isOllieUsageReadAtLimit(data))
    .catch(error => Boolean(getOllieApiError(error).limitReached))
    .then(atLimit => {
      remember(venueId, atLimit)
      return atLimit
    })
  usageReads.set(venueId, { ...cached, pending })
  return pending
}

/**
 * Whether the venue in view (/v/:venueId) is at its Ollie message limit: one
 * shared usage read per venue, updated when the panel finds out. Unknown =
 * false, so a link only hides once the limit is known.
 */
const useOllieAtLimit = () => {
  const venueId =
    typeof window === 'undefined'
      ? null
      : getVenueIdFromPath(window.location?.pathname || '')
  const [read, setRead] = useState({ venueId: null, atLimit: false })

  useEffect(() => {
    if (!venueId) return undefined
    let live = true
    const onChange = (changedVenueId, atLimit) => {
      if (live && changedVenueId === venueId) setRead({ venueId, atLimit })
    }
    listeners.add(onChange)
    readOllieAtLimit(venueId)
      .then(atLimit => onChange(venueId, atLimit))
      .catch(() => {})
    return () => {
      live = false
      listeners.delete(onChange)
    }
  }, [venueId])

  return read.venueId === venueId && read.atLimit
}

export default useOllieAtLimit
